import React from 'react';
import { cn, calculateDailySalesVariance } from '@/lib/utils';
import { Product } from '@/types';

interface StockCount {
  productId: string;
  quantity: number;
}

interface VariancePreviewCardProps {
  products: Product[];
  morningCounts: StockCount[];
  eveningCounts: StockCount[];
  sales: Array<{ productId: string; quantity: number }>;
  restocks: Array<{ productId: string; quantity: number }>;
}

export function VariancePreviewCard({
  products,
  morningCounts,
  eveningCounts,
  sales,
  restocks,
}: VariancePreviewCardProps) {
  // Build maps for quick lookup
  const morningMap = new Map(morningCounts.map(c => [c.productId, c.quantity]));
  const eveningMap = new Map(eveningCounts.map(c => [c.productId, c.quantity]));
  const salesMap = new Map<string, number>();
  const restockMap = new Map<string, number>();

  sales.forEach(s => {
    const current = salesMap.get(s.productId) || 0;
    salesMap.set(s.productId, current + s.quantity);
  });

  restocks.forEach(r => {
    const current = restockMap.get(r.productId) || 0;
    restockMap.set(r.productId, current + r.quantity);
  });

  // Calculate variance for each product using shared function
  const variances = products.map(product => {
    const morning = morningMap.get(product.id) || 0;
    const evening = eveningMap.get(product.id) || 0;
    const restock = restockMap.get(product.id) || 0;
    const recorded = salesMap.get(product.id) || 0;

    // Use shared variance calculation
    const result = calculateDailySalesVariance(
      morning,
      evening,
      restock,
      recorded,
      product.name // For STEMS exemption
    );

    return {
      product,
      movement: result.movement,
      recorded: result.recordedSales,
      variance: result.variance,
      status: result.status,
    };
  }).filter(v => Math.abs(v.variance) > 0.01 && !v.product.isAccumulating); // Only show non-accumulating products with variance

  // Overall status
  const criticalCount = variances.filter(v => v.status === 'CRITICAL').length;
  const warningCount = variances.filter(v => v.status === 'WARNING').length;
  const overallStatus = criticalCount > 0 ? 'CRITICAL' : warningCount > 0 ? 'WARNING' : 'ACCEPTABLE';

  const statusColors = {
    ACCEPTABLE: { bg: 'bg-success/10', border: 'border-success/30', text: 'text-success' },
    WARNING: { bg: 'bg-warning/10', border: 'border-warning/30', text: 'text-warning' },
    CRITICAL: { bg: 'bg-error/10', border: 'border-error/30', text: 'text-error' },
  };

  const colors = statusColors[overallStatus];

  return (
    <div className={cn('rounded-2xl border-2 p-4', colors.bg, colors.border)}>
      <div className="flex items-center gap-3 mb-4">
        <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center text-xl', colors.bg)}>
          {overallStatus === 'ACCEPTABLE' ? '✓' : overallStatus === 'WARNING' ? '⚠️' : '❌'}
        </div>
        <div>
          <h3 className="font-bold text-white">Today's Discrepancy</h3>
          <p className={cn('text-sm', colors.text)}>
            {variances.length === 0
              ? 'All products match records'
              : `${criticalCount} critical, ${warningCount} warning`
            }
          </p>
        </div>
      </div>

      {variances.length > 0 && (
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {variances.map(({ product, movement, recorded, variance, status }) => {
            const itemColors = statusColors[status];
            return (
              <div
                key={product.id}
                className={cn(
                  'flex items-center justify-between p-3 rounded-xl',
                  'bg-dark-lighter/50'
                )}
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-white truncate text-sm">{product.name}</p>
                  <p className="text-xs text-gray-400">
                    Movement: {movement.toFixed(1)} | Recorded: {recorded.toFixed(1)}
                  </p>
                </div>
                <div className={cn('font-bold tabular-nums', itemColors.text)}>
                  {variance > 0 ? '+' : ''}{variance.toFixed(1)}g
                </div>
              </div>
            );
          })}
        </div>
      )}

      {variances.length === 0 && (
        <div className="text-center py-4">
          <p className="text-success font-medium">All products accounted for!</p>
        </div>
      )}
    </div>
  );
}
